import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { BehaviorSubject } from 'rxjs';

export type UiThemeMode = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class UiThemeService {
  private readonly storageKey = 'ui-theme-mode';
  private readonly themeClassPrefix = 'theme-';
  private readonly defaultTheme: UiThemeMode = 'light';

  private readonly themeSubject = new BehaviorSubject<UiThemeMode>(this.defaultTheme);
  readonly theme$ = this.themeSubject.asObservable();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private overlayContainer: OverlayContainer
  ) {}

  init(): void {
    const stored = this.getStoredTheme();
    this.applyTheme(stored);
  }

  toggleTheme(): void {
    const nextTheme: UiThemeMode = this.themeSubject.value === 'dark' ? 'light' : 'dark';
    this.applyTheme(nextTheme);
  }

  setTheme(theme: UiThemeMode): void {
    this.applyTheme(theme);
  }

  private applyTheme(theme: UiThemeMode): void {
    const themeClass = `${this.themeClassPrefix}${theme}`;
    const body = this.document.body;
    body.classList.remove(`${this.themeClassPrefix}light`, `${this.themeClassPrefix}dark`);
    body.classList.add(themeClass);

    // Apply theme to overlay container so dialogs/snackbars inherit correct colors
    const overlayEl = this.overlayContainer.getContainerElement();
    overlayEl.classList.remove(`${this.themeClassPrefix}light`, `${this.themeClassPrefix}dark`);
    overlayEl.classList.add(themeClass);

    this.themeSubject.next(theme);
    try {
      localStorage.setItem(this.storageKey, theme);
    } catch {
      // Ignore storage errors (e.g., privacy mode)
    }
  }

  private getStoredTheme(): UiThemeMode {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // Ignore storage errors
    }
    return this.defaultTheme;
  }
}
